import React, { Component, ReactNode, Suspense } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

interface AsyncViewProps {
  children: ReactNode;
  name: string;
  compact?: boolean;
  onReload?: () => void;
  locale?: 'sv' | 'en';
}

interface AsyncViewState {
  failed: boolean;
}

export default class AsyncView extends Component<AsyncViewProps, AsyncViewState> {
  state: AsyncViewState = { failed: false };

  static getDerivedStateFromError(): AsyncViewState {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error('Application view could not be displayed', {
      view: this.props.name,
      errorType: error.name,
    });
  }

  render() {
    const { children, name, compact, onReload, locale } = this.props;
    const en = locale === 'en';
    const containerSx: SxProps<Theme> = compact
      ? { position: 'fixed', right: 24, bottom: 24, zIndex: 1400, maxWidth: 'calc(100vw - 48px)', width: 360 }
      : { width: '100%', py: 3 };

    if (this.state.failed) {
      return <Box sx={containerSx}>
        <Alert severity="error">
          <AlertTitle>{en ? `${name} could not be displayed.` : `${name} kunde inte visas.`}</AlertTitle>
          {en ? 'Reload the page to try again. Unsaved changes may be lost.'
            : 'Ladda om sidan för att försöka igen. Osparade ändringar kan gå förlorade.'}
          <Box sx={{ mt: 1 }}>
            <Button color="inherit" variant="outlined" onClick={onReload || (() => window.location.reload())}>
              {en ? 'Reload page' : 'Ladda om sidan'}
            </Button>
          </Box>
        </Alert>
      </Box>;
    }

    return <Suspense fallback={
      <Box sx={containerSx}>
        <Box role="status" aria-live="polite" sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
          p: 2, minHeight: compact ? 48 : 160, bgcolor: 'background.paper',
          borderRadius: 1, boxShadow: compact ? 3 : 0,
        }}>
          <CircularProgress size={compact ? 20 : 28} aria-hidden="true" />
          <Typography color="text.secondary">{en ? 'Loading' : 'Laddar'} {name.toLowerCase()}...</Typography>
        </Box>
      </Box>
    }>{children}</Suspense>;
  }
}
